import { useEffect, useState } from "react";
import { Settings, Bell } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { StatusMarker } from "@/components/portal/StatusMarker";
import { Card, Btn, Loader } from "@/components/portal/ui";

const LANGS = [
  { key: "de", label: "Deutsch" },
  { key: "fr", label: "Français" },
  { key: "it", label: "Italiano" },
  { key: "en", label: "English" },
];

export function AccountSettings() {
  const { t, lang, setLang } = useI18n();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/account").then(({ data }) => setData(data)).catch(() => setData(null)); }, []);
  if (!data) return <Loader />;

  const st = { background: "var(--bg)", borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/account/preferences", {
        preferred_lang: data.preferred_lang,
        email_notifications: data.email_notifications,
      });
      if (setLang && data.preferred_lang) setLang(data.preferred_lang);
      toast.success(t("common.saved"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg" data-testid="account-settings">
      <Card className="p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Settings className="h-5 w-5" style={{ color: "var(--accent)" }} />
          <h2 className="font-heading text-lg font-bold" style={{ color: "var(--text)" }}>{t("account.preferences")}</h2>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("account.language")}</label>
          <select data-testid="account-lang-select" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
            value={data.preferred_lang || lang || "de"} onChange={(e) => setData({ ...data, preferred_lang: e.target.value })}>
            {LANGS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
          </select>
        </div>
        <div className="flex items-start justify-between gap-4 p-3 rounded-lg" style={{ background: "var(--surface-alt)" }}>
          <div className="flex items-start gap-2">
            <Bell className="h-4 w-4 mt-0.5" style={{ color: "var(--accent)" }} />
            <div>
              <div className="text-sm font-medium" style={{ color: "var(--text)" }}>{t("account.emailNotifs")}</div>
              <div className="mt-1"><StatusMarker kind="not_configured" testid="account-email-marker" /></div>
              <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>{t("account.emailNotifsHint")}</p>
            </div>
          </div>
          <button
            type="button"
            data-testid="account-email-toggle"
            onClick={() => setData({ ...data, email_notifications: !data.email_notifications })}
            className="relative h-6 w-11 shrink-0 rounded-full transition-colors"
            style={{ background: data.email_notifications ? "var(--accent)" : "var(--border)" }}
            aria-pressed={data.email_notifications}
          >
            <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all" style={{ left: data.email_notifications ? "22px" : "2px" }} />
          </button>
        </div>
        <Btn onClick={save} disabled={saving} className="w-full" data-testid="account-prefs-save-btn">{t("common.save")}</Btn>
      </Card>
    </div>
  );
}
